import React from "react";
import { motion } from "motion/react";
import clsx from "clsx";

interface FeatureCardProps {
  icon: React.ComponentType<{ className?: string }>;
  title: string;
  description: string;
  iconColor?: string;
  variant?: "card" | "inline";
  className?: string;
}

export const FeatureCard: React.FC<FeatureCardProps> = ({
  icon: Icon,
  title,
  description,
  iconColor = "bg-gradient-to-br from-slate-600 to-gray-700 dark:from-slate-500 dark:to-gray-600",
  variant = "card",
  className,
}) => {
  if (variant === "inline") {
    return (
      <motion.div
        className={clsx("group relative h-full", className)}
        initial={{ opacity: 0, y: 20 }}
        whileInView={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5, ease: "easeOut" }}
        viewport={{ once: true }}
      >
        <div className="relative h-full rounded-xl border border-gray-200/60 dark:border-gray-700/60 bg-white/80 dark:bg-gray-800/80 backdrop-blur-sm p-4 sm:p-6 hover:border-gray-300/60 dark:hover:border-gray-600/60 transition-all duration-300 group-hover:shadow-lg group-hover:shadow-gray-500/5 dark:group-hover:shadow-gray-900/10">
          <div className="absolute inset-0 bg-gradient-to-br from-gray-50/40 via-slate-50/30 to-gray-100/40 dark:from-gray-800/20 dark:via-gray-700/10 dark:to-gray-800/20 rounded-xl opacity-0 group-hover:opacity-100 transition-opacity duration-300" />

          <div className="relative flex items-center gap-4 h-full">
            <div className={clsx("flex items-center justify-center w-10 h-10 rounded-lg flex-shrink-0", iconColor)}>
              <Icon className="w-5 h-5 text-white" />
            </div>

            <div className="flex-1 space-y-1 sm:space-y-2">
              <h6 className="text-base sm:text-lg font-semibold text-gray-900 dark:text-gray-100 tracking-tight leading-tight">
                {title}
              </h6>
              {description && (
                <p className="text-gray-600 dark:text-gray-400 leading-relaxed text-sm">
                  {description}
                </p>
              )}
            </div>
          </div>
        </div>
      </motion.div>
    );
  }

  return (
    <motion.div
      className={clsx("group relative", className)}
      initial={{ opacity: 0, y: 20 }}
      whileInView={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.5, ease: "easeOut" }}
      viewport={{ once: true }}
    >
      <div className="relative rounded-xl border border-gray-200/60 dark:border-gray-700/60 bg-white/80 dark:bg-gray-800/80 backdrop-blur-sm p-6 sm:p-8 hover:border-gray-300/60 dark:hover:border-gray-600/60 transition-all duration-300 group-hover:shadow-lg group-hover:shadow-gray-500/5 dark:group-hover:shadow-gray-900/10 h-full">
        <div className="absolute inset-0 bg-gradient-to-br from-gray-50/40 via-slate-50/30 to-gray-100/40 dark:from-gray-800/20 dark:via-gray-700/10 dark:to-gray-800/20 rounded-xl opacity-0 group-hover:opacity-100 transition-opacity duration-300" />

        <div className="relative space-y-4">
          <div className={clsx("flex items-center justify-center w-12 h-12 rounded-lg transition-transform duration-200 group-hover:scale-105", iconColor)}>
            <Icon className="w-6 h-6 text-white" />
          </div>

          <div className="space-y-2">
            <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100 tracking-tight">
              {title}
            </h3>
            <p className="text-gray-600 dark:text-gray-400 leading-relaxed text-sm">
              {description}
            </p>
          </div>
        </div>
      </div>
    </motion.div>
  );
}; 